/**
 * Une carte repliée : une ligne de résumé, et le détail au clic.
 *
 * Hedi, le 27 septembre 2026 : *« Dans entrainements > Composés ne pas afficher
 * le détail de l'entrainement. Juste la date, les liens vers les PDFs, si ça a
 * été corrigé ou pas. Quand je clique dessus, là je vois les détails comme
 * maintenant. »*, puis *« Même chose dans à Valider. »* Les deux écrans de la
 * V2 s'en servent, d'où un composant et non deux copies.
 *
 * **Les liens PDF sont à côté du bouton, jamais dedans.** Un lien dans un bouton
 * est du HTML invalide, et un clic sur le lien déplierait la carte en plus
 * d'ouvrir le PDF. Un clic sur le blanc de la ligne déplie aussi — c'est ce que
 * « cliquer dessus » veut dire sur un téléphone —, mais seulement quand il
 * tombe sur la ligne elle-même et non sur ce qu'elle contient.
 *
 * **Le détail replié reste monté, caché par `hidden`.** Le démonter ferait
 * perdre un verdict choisi mais pas encore confirmé, ou une question en cours
 * de saisie, au premier repli ; et une image cachée en `loading="lazy"` n'est
 * pas chargée tant qu'elle ne s'affiche pas.
 */

import { useId, useState, type ReactNode } from "react";

import { Card } from "@/ui/primitives";

export function Depliable({
  titre,
  etat,
  liens,
  children,
}: {
  titre: ReactNode;
  /** Où en est l'élément, en toutes lettres : « corrigé », « 3 jugés sur 8 ». */
  etat: ReactNode;
  liens?: ReactNode;
  children: ReactNode;
}) {
  const [ouvert, setOuvert] = useState(false);
  const detail = useId();
  const basculer = () => setOuvert((avant) => !avant);

  return (
    <Card>
      <div
        className="depliable__ligne"
        onClick={(evenement) => {
          if (evenement.target === evenement.currentTarget) basculer();
        }}
      >
        <button
          type="button"
          className="depliable__bouton"
          aria-expanded={ouvert}
          aria-controls={detail}
          onClick={basculer}
        >
          <span className="depliable__titre">{titre}</span>
          <span className="depliable__etat">{etat}</span>
          <span className="depliable__signe" aria-hidden="true">
            {ouvert ? "▾" : "▸"}
          </span>
        </button>
        {liens ? <div className="depliable__liens">{liens}</div> : null}
      </div>
      <div id={detail} className="depliable__detail" hidden={!ouvert}>
        {children}
      </div>
    </Card>
  );
}
